import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import './AllUsers.css';
import LocationFilter from '../locaties/LocationFilter.js';

function AllUsers() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');
  const { userInfo, isAuthenticated } = useAuth();
  const role = userInfo?.role || '';
  const is_single_user = userInfo?.is_single_user;

  const [users, setUsers] = useState([]);
  const [zoekterm, setZoekterm] = useState('');
  const [debouncedZoekterm, setDebouncedZoekterm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false
  });

  axios.defaults.withCredentials = true;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedZoekterm(zoekterm.trim());
    }, 400);

    return () => clearTimeout(timer);
  }, [zoekterm]);

  const getLocationNames = (ids) => {
    if (!ids || ids.length === 0) return '—';
    const names = ids
      .map((id) => {
        const loc = locations.find((l) => l.location_id === id);
        return loc ? loc.location_name : id;
      })
      .filter(Boolean);
    return names.join(', ');
  };

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/user/all`,
        {
          params: {
            page,
            limit,
            search: debouncedZoekterm,
            location_id: locationFilter,
            role: roleFilter,
            status: statusFilter
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setUsers(Array.isArray(response.data?.data) ? response.data.data : []);
      setPagination(
        response.data?.pagination || {
          page: 1,
          limit,
          total: 0,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false
        }
      );
    } catch (error) {
      console.error('Error fetching users', error);
      setUsers([]);
      setPagination({
        page: 1,
        limit,
        total: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false
      });
    } finally {
      setIsLoading(false);
    }
  }, [token, page, limit, debouncedZoekterm, locationFilter, roleFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (role === 'super_admin' || role === 'eigenaar') {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/location/allLocations`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          const locs = res.data.data || res.data;
          setLocations(Array.isArray(locs) ? locs : []);
        })
        .catch((err) => {
          console.error('Error fetching locations', err);
          setLocations([]);
        });
    }
  }, [role, token]);

  const handleDelete = async (id) => {
    if (!window.confirm(`Weet je zeker dat je gebruiker met ID ${id} wilt verwijderen?`)) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/user/delete/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
      alert(`Gebruiker met ID ${id} is verwijderd.`);
    } catch (err) {
      console.error('Fout bij verwijderen gebruiker', err);
      alert('Verwijderen mislukt.');
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm(`Weet je zeker dat je gebruiker met ID ${id} wilt deactiveren?`)) return;
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/user/deactivate/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
      alert(`Gebruiker met ID ${id} is gedeactiveerd.`);
    } catch (err) {
      console.error('Fout bij deactiveren gebruiker', err);
      alert('Deactiveren mislukt.');
    }
  };

  const handleActivate = async (id) => {
    if (!window.confirm(`Weet je zeker dat je gebruiker met ID ${id} wilt activeren?`)) return;
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/user/activate/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
      alert(`Gebruiker met ID ${id} is geactiveerd.`);
    } catch (err) {
      console.error('Fout bij activeren gebruiker', err);
      alert('Activeren mislukt.');
    }
  };

  const handleReset2FA = async (id) => {
    if (!window.confirm(`Weet je zeker dat je 2FA wilt resetten voor gebruiker met ID ${id}?`)) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/2fa/${id}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
      alert(`2FA is gereset voor gebruiker met ID ${id}.`);
    } catch (err) {
      console.error('Fout bij resetten 2FA', err);
      alert('2FA reset mislukt.');
    }
  };

  const handleView = (id) => navigate(`/user/view/${id}`);
  const showLocationColumn = (role === 'super_admin' || role === 'eigenaar') && !is_single_user;

  const getRoleBadgeClass = (userRole) => {
    const map = {
      super_admin: 'badge-role-super',
      locatie_admin: 'badge-role-locatie',
      docent: 'badge-role-docent',
      ouder: 'badge-role-ouder',
      leerling: 'badge-role-leerling',
    };
    return map[userRole] || 'badge-role-default';
  };

  const ActionButtons = ({ user }) => (
    <div className="d-flex flex-wrap gap-1">
      {role !== 'eigenaar' && <Link
        to={`/user/edit/${user.user_id}`}
        className="btn btn-sm btn-warning rounded-0"
        style={{ fontSize: '10px', padding: '2px 5px' }}
        onClick={(e) => e.stopPropagation()}
      >
        Bewerk
      </Link>}
      {role !== 'eigenaar' && <button
        type="button"
        className="btn btn-sm btn-danger rounded-0"
        style={{ fontSize: '10px', padding: '2px 5px' }}
        onClick={(e) => { e.stopPropagation(); handleDelete(user.user_id); }}
      >
        Verwijder
      </button>}
      {user.status === 'active' ? (
        <button
          type="button"
          className="btn btn-sm btn-secondary rounded-0"
          style={{ fontSize: '10px', padding: '2px 5px' }}
          onClick={(e) => { e.stopPropagation(); handleDeactivate(user.user_id); }}
        >
          Deactiveer
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-sm btn-success rounded-0"
          style={{ fontSize: '10px', padding: '2px 5px' }}
          onClick={(e) => { e.stopPropagation(); handleActivate(user.user_id); }}
        >
          Activeer
        </button>
      )}
      {user.two_factor_enabled && (
        <button
          type="button"
          className="btn btn-sm btn-outline-info rounded-0"
          style={{ fontSize: '10px', padding: '2px 5px' }}
          onClick={(e) => { e.stopPropagation(); handleReset2FA(user.user_id); }}
        >
          Reset 2FA
        </button>
      )}
    </div>
  );

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const renderPaginationButtons = () => {
    if ((pagination.totalPages || 1) <= 1) return null;

    const currentPage = pagination.page || 1;
    const totalPages = pagination.totalPages || 1;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages, 5);
    }

    if (currentPage >= totalPages - 1) {
      startPage = Math.max(1, totalPages - 4);
    }

    const buttons = [];

    for (let i = startPage; i <= endPage; i += 1) {
      buttons.push(
        <button
          key={i}
          type="button"
          className={`btn btn-sm ${i === currentPage ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => setPage(i)}
        >
          {i}
        </button>
      );
    }

    return buttons;
  };

  const startRecord = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endRecord =
    pagination.total === 0
      ? 0
      : Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div className="all-users">
      {isAuthenticated ? (
        <>
          <h1>Overzicht Van Alle Gebruikers</h1>

          <div className="d-flex justify-content-end mb-3">
            {role !== 'eigenaar' && <Link to="/user/new" className="btn btn-success">
              <i className="bi bi-plus-circle me-2"></i>Nieuwe gebruiker
            </Link>}
          </div>

          {showLocationColumn && (
            <div className="mb-3">
              <LocationFilter
                userRole={role}
                locationFilter={locationFilter}
                setLocationFilter={(value) => {
                  setLocationFilter(value);
                  setPage(1);
                }}
                locations={locations}
              />
            </div>
          )}

          <div className="row mb-3">
            <div className="col-12 col-md-4 mb-2">
              <input
                type="text"
                className="form-control rounded-0"
                placeholder="Zoek op naam, email of rol"
                value={zoekterm}
                onChange={(e) => {
                  setZoekterm(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="col-12 col-md-2 mb-2">
              <select
                className="form-select rounded-0"
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setPage(1);
                }}
                aria-label="Filter op rol"
              >
                <option value="all">Alle rollen</option>
                <option value="eigenaar">Eigenaar</option>
                <option value="super_admin">Super admin</option>
                <option value="locatie_admin">Locatie-admin</option>
                <option value="docent">Docent</option>
                <option value="ouder">Ouder</option>
                <option value="leerling">Leerling</option>
              </select>
            </div>

            <div className="col-12 col-md-2 mb-2">
              <select
                className="form-select rounded-0"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                aria-label="Filter op status"
              >
                <option value="all">Alle statussen</option>
                <option value="active">Actief</option>
                <option value="inactive">Inactief</option>
              </select>
            </div>

            <div className="col-12 col-md-2 mb-2">
              <select
                className="form-select rounded-0"
                value={limit}
                onChange={handleLimitChange}
              >
                <option value={10}>Toon 10</option>
                <option value={20}>Toon 20</option>
                <option value={50}>Toon 50</option>
                <option value={100}>Toon 100</option>
              </select>
            </div>

            <div className="col-12 col-md-2 text-md-end">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setZoekterm('');
                  setLocationFilter('all');
                  setRoleFilter('all');
                  setStatusFilter('all');
                  setLimit(20);
                  setPage(1);
                }}
              >
                Filters wissen
              </button>
            </div>
          </div>

          <div className="row mb-3 align-items-center">
            <div className="col-12">
              <small className="text-muted">
                Toont {startRecord} t/m {endRecord} van {pagination.total || 0} gebruikers
              </small>
            </div>
          </div>

          {isLoading ? (
            <p>Loading...</p>
          ) : (
            <>
              <div className="table-responsive d-none d-md-block">
                <table className="table table-striped table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Naam</th>
                      <th>Email</th>
                      <th>Rol</th>
                      <th>Status</th>
                      {showLocationColumn && <th>Locaties</th>}
                      <th>Laatste Login</th>
                      <th>Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr
                        key={user.user_id}
                        onClick={() => handleView(user.user_id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{user.user_id}</td>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`user-role-badge ${getRoleBadgeClass(user.role)}`}>
                            {user.role}
                          </span>
                        </td>
                        <td>
                          <span className={`user-status-badge ${user.status === 'active' ? 'status-active' : 'status-inactive'}`}>
                            {user.status}
                          </span>
                        </td>
                        {showLocationColumn && <td>{getLocationNames(user.location_ids)}</td>}
                        <td>{user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Nooit'}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <ActionButtons user={user} />
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={showLocationColumn ? 8 : 7} className="text-center">
                          Geen gebruikers gevonden.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="d-md-none user-cards">
                {users.length === 0 && (
                  <p className="text-center text-muted mt-3">Geen gebruikers gevonden.</p>
                )}
                {users.map((user) => (
                  <div
                    key={user.user_id}
                    className="user-card"
                    onClick={() => handleView(user.user_id)}
                  >
                    <div className="user-card-header">
                      <div className="user-card-info">
                        <div className="user-card-name">{user.name}</div>
                        <div className="user-card-email">{user.email}</div>
                      </div>
                      <div className="d-flex flex-column align-items-end gap-1">
                        <span className={`user-role-badge ${getRoleBadgeClass(user.role)}`}>
                          {user.role}
                        </span>
                        <span className={`user-status-badge ${user.status === 'active' ? 'status-active' : 'status-inactive'}`}>
                          {user.status}
                        </span>
                      </div>
                    </div>
                    {showLocationColumn && (
                      <div className="user-card-meta">
                        <i className="bi bi-geo-alt me-1"></i>
                        {getLocationNames(user.location_ids)}
                      </div>
                    )}
                    <div className="user-card-meta">
                      <i className="bi bi-clock me-1"></i>
                      {user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Nooit'}
                    </div>
                    <div className="user-card-actions" onClick={(e) => e.stopPropagation()}>
                      <ActionButtons user={user} />
                    </div>
                  </div>
                ))}
              </div>

              {(pagination.totalPages || 1) > 1 && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mt-3">
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={!pagination.hasPrevPage}
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    >
                      Vorige
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={!pagination.hasNextPage}
                      onClick={() => setPage((prev) => prev + 1)}
                    >
                      Volgende
                    </button>
                  </div>

                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    {renderPaginationButtons()}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <p>You are not authenticated.</p>
      )}
    </div>
  );
}

export default AllUsers;
